import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { mainCategoriesAPI, adminAPI } from '@/lib/api';
import { toast } from '@/components/ui/sonner';

const ICON_OPTIONS = [
  'Beef', 'Leaf', 'Sparkles', 'Home',
  'Apple', 'HeartPulse', 'Flower', 'ShoppingBag',
  'Coffee', 'Wine', 'Carrot', 'Pill',
  'Shirt', 'Baby', 'Bone', 'Sofa', 'Lightbulb', 'PawPrint',
];

const EMPTY = { name: '', icon: 'Sparkles', sort_order: 50, description: '' };

export default function AdminMainCategories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const fetchItems = () => {
    setLoading(true);
    mainCategoriesAPI.list()
      .then(r => setItems(r.data))
      .catch(() => toast.error('Failed to load main categories'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchItems(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, sort_order: (items.length + 1) * 10 });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name,
      icon: item.icon || 'Sparkles',
      sort_order: item.sort_order || 0,
      description: item.description || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await adminAPI.updateMainCategory(editing.id, form);
        toast.success('Main category updated');
      } else {
        await adminAPI.createMainCategory(form);
        toast.success('Main category created');
      }
      setDialogOpen(false);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? Any categories or suppliers linked to it will be unlinked (not deleted).`)) return;
    try {
      await adminAPI.deleteMainCategory(item.id);
      toast.success('Main category deleted');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="admin-main-categories-page" className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-light text-foreground">Main Categories</h1>
          <p className="font-body text-sm text-foreground/50 mt-1">
            Top-level groups shown in the customer storefront menu (e.g. Food, Health, Body & Beauty).
            <br />Sub-categories and suppliers can be assigned to one main category each.
          </p>
        </div>
        <Button
          data-testid="add-main-category-btn"
          onClick={openCreate}
          className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light gap-2 font-body font-semibold"
        >
          <Plus className="h-5 w-5" strokeWidth={2} /> Add Main Category
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-card border border-border">
          <p className="font-body text-sm text-foreground/50">No main categories yet.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="main-categories-list">
          {items.map(item => (
            <div
              key={item.id}
              data-testid={`main-category-${item.slug}`}
              className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border hover:border-teal/30 transition-colors"
            >
              <GripVertical className="h-5 w-5 text-foreground/30 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-body font-bold text-foreground">{item.name}</p>
                <p className="font-body text-xs text-foreground/40">
                  slug: <span className="text-teal">{item.slug}</span>
                  {' · '}icon: <span className="text-gold">{item.icon || '—'}</span>
                  {' · '}sort: {item.sort_order}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openEdit(item)}
                data-testid={`edit-main-category-${item.slug}`}
                className="rounded-full h-9 w-9 p-0 hover:bg-muted"
              >
                <Pencil className="h-4 w-4" strokeWidth={2} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDelete(item)}
                data-testid={`delete-main-category-${item.slug}`}
                className="rounded-full h-9 w-9 p-0 hover:bg-red-900/20 text-red-400"
              >
                <Trash2 className="h-4 w-4" strokeWidth={2} />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background rounded-2xl max-w-md" data-testid="main-category-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">
              {editing ? 'Edit Main Category' : 'Add Main Category'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-body text-sm text-foreground">Name *</Label>
              <Input
                data-testid="main-category-name-input"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="mt-1 rounded-xl bg-card border-border font-body"
                placeholder="e.g. Pets"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-body text-sm text-foreground">Icon</Label>
                <select
                  data-testid="main-category-icon-select"
                  value={form.icon}
                  onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}
                  className="mt-1 w-full rounded-xl bg-card border border-border px-3 py-2 font-body text-sm text-foreground"
                >
                  {ICON_OPTIONS.map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="font-body text-sm text-foreground">Sort Order</Label>
                <Input
                  type="number"
                  data-testid="main-category-sort-input"
                  value={form.sort_order}
                  onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                  className="mt-1 rounded-xl bg-card border-border font-body"
                />
              </div>
            </div>
            <div>
              <Label className="font-body text-sm text-foreground">Description</Label>
              <Input
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="mt-1 rounded-xl bg-card border-border font-body"
                placeholder="Optional"
              />
            </div>
            <p className="font-body text-[11px] text-foreground/40">
              Use a Lucide icon name (e.g. Beef, Leaf, Sparkles, Home, PawPrint).
              Unknown icons render as plain text and won't break the storefront.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="rounded-full border-border font-body text-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-main-category-btn"
              className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
